import fetch from 'isomorphic-unfetch'
import cookies from '../cookies'
import getEnv from '../env'
import buildQueryString from '../buildQueryString'
import getAccessToken from '../getAccessToken'
import { playerApiRequest, playerApiReceive } from '../../actions'
import {
  DEFAULT_GROUPTOKEN,
  UK_LOGGED_OUT_GROUPTOKEN,
  CLEARCACHE_KEY,
  PLAYER_ERROR_CODES_MAP,
} from '../../constants'
import getApiUrl from '../getApiUrl'
import getQaCookies from '../qaCookies'
import logError from '../logError'

const playerApi = ctx => ({
  dispatch,
  path = '',
  query = {},
  sendGroupToken = false,
  sendAuthHeader = false,
}) => {
  const getHeaders = psEnabled => ({
    'Stv-Drm': 'true',
    ...(psEnabled && { 'Stv-PremierSportsAware': 'true' }),
    ...(
      ctx?.req?.headers && {
        http_origin: ctx.req.headers.http_origin,
        'x-forwarded-for': ctx.req.headers['x-forwarded-for'],
        enableTiering: true,
      }
    ),
  })

  const getGroupToken = () => cookies.get('STV_GROUPTOKEN', ctx?.req)

  const checkIsLoggedIn = groupToken => Boolean(
    Number(groupToken.split('').pop())
  )

  const logRequest = ({ url, environment }) => {
    if (environment === 'development' || environment === 'staging' || environment === 'testing') {
      console.log(`--- Request made to: ${url} | ${new Date()}`) // eslint-disable-line no-console
    }
  }

  const store = ctx?.reduxStore
    ? ctx?.reduxStore?.getState()
    : window?.__NEXT_DATA__?.props?.initialReduxState

  const psEnabled = store?.featureFlags?.psEnabled === true || false
  const headers = getHeaders(psEnabled)
  const environment = getEnv()
  let tpPrefix = null
  let clearCache = null
  const qaCookies = getQaCookies(ctx)
  if (qaCookies !== null) {
    ({ tpPrefix = '', clearCache = false } = qaCookies)
  }
  const normalizedQueryGroupToken = query.groupToken === 'undefined' ? null : query.groupToken
  const groupToken = normalizedQueryGroupToken
    || (cookieToken => (cookieToken === 'undefined' ? null : cookieToken))(getGroupToken())
    || (sendGroupToken ? UK_LOGGED_OUT_GROUPTOKEN : DEFAULT_GROUPTOKEN)
  const isLoggedIn = checkIsLoggedIn(groupToken)
  const shouldSendGroupToken = query.groupToken || isLoggedIn || sendGroupToken
  const storeResponseInRedux = Boolean(dispatch)
  if (sendAuthHeader) {
    const accessToken = getAccessToken(ctx?.req)
    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`
    }
  }

  const url = buildRequestUrl({
    environment: tpPrefix === 'prod' ? 'production' : environment,
    path,
    query: {
      ...query,
      ...(shouldSendGroupToken && { groupToken }),
      clearcache: clearCache ? CLEARCACHE_KEY : null,
    },
    tpPrefix,
  })

  const request = () => {
    logRequest({ url, environment })

    return fetchWithTimeout({
      headers,
      url,
    })
  }

  if (storeResponseInRedux) {
    return dispatch(() => {
      dispatch(
        playerApiRequest(path)
      )
      return request()
        .then((response) => {
          dispatch(
            playerApiReceive({ path, response })
          )
          return response
        })
    })
  }

  return request()
}

export default playerApi

export function buildRequestUrl({
  environment,
  query,
  path,
  tpPrefix = null,
}) {
  const tpPrefixStr = tpPrefix && tpPrefix !== 'prod' ? `${tpPrefix}.` : ''
  const base = environment === 'testing' ? `http://localhost:3000/mockPlayerApi${path}` : [
    `https://${tpPrefixStr}player.api.`,
    getApiUrl(environment),
    '/v1',
    path,
  ].join('')

  const requestQuery = buildQueryString(query)
  const requestUrl = [
    base,
    requestQuery && `${requestQuery}`,
  ].join('')
  return requestUrl
}

// bumped to 8s due to timeout errors on summary page
export function fetchWithTimeout({
  url = '',
  time = 8000,
  headers,
}) {
  const timeout = new Promise(resolve => setTimeout(() => {
    const error = {
      success: false,
      reason: {
        code: PLAYER_ERROR_CODES_MAP[408],
        message: `Timed out after ${time}ms: ${url}`,
      },
    }
    resolve(error)
  }, time))
  return Promise.race([
    fetch(url, {
      headers,
      cache: 'no-cache',
    })
      .then(async (r) => {
        const httpStatus = r?.status
        try {
          const json = await r.json()
          if (!r.ok) {
            logError({
              source: 'player-api',
              url,
              message: json?.reason?.message,
              code: PLAYER_ERROR_CODES_MAP[json?.reason?.code] || json?.reason?.code,
              httpStatus,
            })
          }
          return json
        } catch (err) {
          // not able to parse JSON, usually because we're getting an HTML error page instead
          logError({
            source: 'player-api',
            url,
            message: err.message,
            code: err.type,
            httpStatus,
          })
          return {
            success: false,
            reason: {
              code: 500,
              message: err.message,
            },
          }
        }
      }),
    timeout,
  ])
}
